import { assertNonNullable } from '@disy/lib/custom-error';
import { MIMETYPE_JSON } from '@disy/lib/media-type';

import type { AttributeGroupAssignment } from 'cadenza/admin/analytics-extensions/analytics-extension-dto';
import { ajax } from 'cadenza/api-client/api';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';
import type { ObjecttypeDataScope } from 'cadenza/api-client/model/objecttype-data-scope.gen';
import type { WorkOrderId } from 'cadenza/api-client/workorder-api';
import type { IsochroneLocationType } from 'cadenza/map/routing/model/isochrone-location-type.gen';
import type { IsochroneSmoothing } from 'cadenza/map/routing/model/isochrone-smoothing.gen';
import type { IsochroneType } from 'cadenza/map/routing/model/isochrone-type.gen';
import type { RoutingAvoidFeatureOption } from 'cadenza/map/routing/model/routing-avoid-feature-option.gen';
import type { RoutingBorderOption } from 'cadenza/map/routing/model/routing-border-option.gen';
import type { RoutingMode } from 'cadenza/map/routing/model/routing-mode.gen';
import type { RoutingProfile } from 'cadenza/map/routing/model/routing-profile.gen';
import type { AttributeName } from 'cadenza/model/attribute-name.gen';
import type { AnalyticsExtensionId } from 'cadenza/repository/model/analytics-extension-id.gen';
import type { DataConfidentialityLevelId } from 'cadenza/repository/model/data-confidentiality-level-id.gen';
import type { DataProtectionContextId } from 'cadenza/repository/model/data-protection-context-id.gen';
import type { DataProtectionLabelId } from 'cadenza/repository/model/data-protection-label-id.gen';
import type { EnrichmentType } from 'cadenza/repository/model/enrichment-type';
import type { MeasureAggregation } from 'cadenza/repository/model/measure-aggregation.gen';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import type { WorkbookId } from 'cadenza/repository/model/workbook-id.gen';
import { type WorkbookNameAndHash, workbookNameAndHashString } from 'cadenza/repository/model/workbook-name-and-hash';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import type { EnrichmentBufferUnit } from 'cadenza/workbook/enrichment/model/enrichment-buffer-unit.gen';
import type { EnrichmentCandidateDto } from 'cadenza/workbook/enrichment/model/enrichment-candidate-dto';
import type { IsochronesEnrichmentConfigDto } from 'cadenza/workbook/enrichment/model/enrichment-config-dto';
import type { DataScopeContextMapping } from 'cadenza/workbook/enrichment/model/enrichment-data-scope-context-dto';
import type { EnrichmentDto } from 'cadenza/workbook/enrichment/model/enrichment-dto';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';

export type ObjecttypeDataScopeMapping = {
  [key in ObjecttypeIdString]: ObjecttypeDataScope;
};

export interface EnrichmentDataScopeSettings {
  objecttypeDataScopeMapping: ObjecttypeDataScopeMapping;
  workbookNameAndHash: WorkbookNameAndHash;
  worksheetId: WorksheetId;
}

/**
 * We need this type of DTO because DTOs like ReverseGeocodingEnrichmentRequestDto are located into modules
 * that cannot have a dependency on DataProtectionDescriptionDto which is located in a web module: cadenza-repository-web
 *
 * @see net/disy/cadenza/repository/api/dataprotection/DataProtectionDescription.java
 */
interface DataProtectionDescription {
  dataConfidentialityLevelId: DataConfidentialityLevelId;
  dataProtectionLabels: DataProtectionLabelId[];
}

/** @see net/disy/cadenza/selfservice/enrichment/data/IEnrichmentRequest.java */
export interface BasicEnrichmentRequest {
  dataScopeSettings: EnrichmentDataScopeSettings;
  dataProtectionContextId?: DataProtectionContextId;
  dataProtectionDescription?: DataProtectionDescription;
  enrichedObjecttypeId: ObjecttypeIdString;
  associatedBasicObjecttypeId: ObjecttypeIdString;
  groupAttributes: boolean;
  targetObjecttypeName: string;
  workbookId: WorkbookId;
  existingEnrichmentObjecttypeId?: ObjecttypeIdString;
}

interface IsochronesRequest {
  avoidFeatureOptions: RoutingAvoidFeatureOption[];
  borderOption: RoutingBorderOption;
  enrichedAttributeId: AttributeId;
  isochroneType: IsochroneType;
  locationType?: IsochroneLocationType;
  routingMode: RoutingMode;
  routingProfile: RoutingProfile;
  smoothing: IsochroneSmoothing;
  time?: Date;
  zoneValue: number;
}

/** @see net/disy/cadenza/selfservice/imports/web/base/dto/MeasuresFromSpatialIntersectionRequestDto.java */
interface MeasuresFromSpatialIntersectionRequestDto extends BasicEnrichmentRequest {
  enrichedAttributeId: AttributeId;
  providerAttribute: AttributeId;
  useBuffer: boolean;
  bufferUnit: EnrichmentBufferUnit;
  buffer: number;
  calculations: MeasuresFromSpatialIntersectionCalculationDto[];
}

/** @see net/disy/cadenza/selfservice/enrichment/dto/MeasuresFromSpatialIntersectionCalculationDto.java */
export interface MeasuresFromSpatialIntersectionCalculationDto {
  calculationName: string;
  measureProvidingMeasureAttribute: AttributeId;
  aggregationFunction: MeasureAggregation;
  proportionalCalculation?: boolean;
  enrichmentAttributeName?: AttributeName;
}

export type EnrichmentRequest<T extends EnrichmentType> =
  T extends 'MeasuresFromSpatialIntersectionEnrichment' ? MeasuresFromSpatialIntersectionRequestDto
  : T extends 'AnalyticsExtensionEnrichment' ? AnalyticsExtensionEnrichmentRequestDto
  : T extends 'ReverseGeocodingEnrichment' ? ReverseGeocodingEnrichmentRequestDto
  : T extends 'StructuredGeocodingEnrichment' ? StructuredGeocodingEnrichmentRequestDto
  : T extends 'UnstructuredGeocodingEnrichment' ? UnstructuredGeocodingEnrichmentRequestDto
  : T extends 'IsochronesEnrichment' ? IsochronesEnrichmentRequestDto
  : T extends 'IpGeocodingEnrichment' ? IpGeocodingEnrichmentRequestDto
  : never;

interface CommonAnalyticsExtensionEnrichmentRequest {
  analyticsExtensionId: AnalyticsExtensionId;
  attributeGroupAssignments: AttributeGroupAssignment[];
  parameters: { [key: string]: number | string | boolean | undefined };
}

/** @see src/main/java/net/disy/cadenza/analytics/extensions/enrichment/AnalyticsExtensionEnrichmentRequestDto.java */
interface AnalyticsExtensionEnrichmentRequestDto
  extends BasicEnrichmentRequest, CommonAnalyticsExtensionEnrichmentRequest {}

/** @see src/main/java/net/disy/cadenza/selfservice/enrichment/reversegeocoding/data/ReverseGeocodingEnrichmentRequestDto.java */
interface ReverseGeocodingEnrichmentRequestDto extends BasicEnrichmentRequest {
  enrichedAttributeId: AttributeId;
}

/** @see src/main/java/net/disy/cadenza/selfservice/enrichment/isochrones/dto/IsochronesEnrichmentRequestDto.java */
interface IsochronesEnrichmentRequestDto extends BasicEnrichmentRequest, IsochronesRequest {}

/** @see src/main/java/net/disy/cadenza/selfservice/enrichment/geocoding/ip/dto/IpGeocodingEnrichmentRequestDto.java */
interface IpGeocodingEnrichmentRequestDto extends BasicEnrichmentRequest {
  ipAddressAttribute: AttributeId;
}

/** @see src/main/java/net/disy/cadenza/selfservice/enrichment/geocoding/dto/StructuredGeocodingEnrichmentRequestDto.java */
interface StructuredGeocodingEnrichmentRequestDto extends BasicEnrichmentRequest {
  countryCodeAttribute?: AttributeId;
  countrySubdivisionAttribute?: AttributeId;
  countrySecondarySubdivisionAttribute?: AttributeId;
  postalCodeAttribute?: AttributeId;
  municipalityAttribute?: AttributeId;
  settlementAttribute?: AttributeId;
  streetAttribute?: AttributeId;
  housenumberAttribute?: AttributeId;
  housenumberSuffixAttribute?: AttributeId;
}

/** @see src/main/java/net/disy/cadenza/selfservice/enrichment/geocoding/dto/UnstructuredGeocodingEnrichmentRequestDto.java */
interface UnstructuredGeocodingEnrichmentRequestDto extends BasicEnrichmentRequest {
  unstructuredAddressAttribute?: AttributeId;
  addressContext?: string;
  worldBoxSrs?: string;
  worldBoxEnvelope?: WorldBoxDto;
}

export function getEnrichmentCandidates(
  workbookNameAndHash: WorkbookNameAndHash,
  objecttypeId: ObjecttypeIdString,
): Promise<EnrichmentCandidateDto[]> {
  return ajax(
    `/workbooks/${workbookNameAndHashString(workbookNameAndHash)}/objecttypes/${btoa(objecttypeId)}/enrichment-candidates`,
    { type: 'GET' },
  );
}

export function getIsochronesEnrichmentConfig(): Promise<IsochronesEnrichmentConfigDto> {
  return ajax('/config/IsochronesEnrichment', { type: 'GET' });
}

export function startEnrichment<T extends EnrichmentType>(
  workbookNameAndHash: WorkbookNameAndHash,
  enrichmentType: T,
  enrichmentDto: EnrichmentRequest<T>,
) {
  const objecttypeId = enrichmentDto.enrichedObjecttypeId;
  return ajax<WorkOrderId>(
    `/workbooks/${workbookNameAndHashString(workbookNameAndHash)}/objecttypes/${btoa(objecttypeId)}/enrichment/${enrichmentType}/run-work-orders`,
    {
      type: 'POST',
      contentType: MIMETYPE_JSON,
      data: JSON.stringify(enrichmentDto),
    },
  );
}

export function rerunEnrichment<T extends EnrichmentType>(
  workbookNameAndHash: WorkbookNameAndHash,
  objecttypeId: ObjecttypeIdString,
  enrichmentType: T,
  enrichmentRequest: EnrichmentRequest<T>,
) {
  assertNonNullable(enrichmentRequest.existingEnrichmentObjecttypeId);
  return ajax<WorkOrderId>(
    `/workbooks/${workbookNameAndHashString(workbookNameAndHash)}/objecttypes/${btoa(objecttypeId)}/enrichment/${enrichmentType}/rerun-work-orders`,
    {
      type: 'POST',
      contentType: MIMETYPE_JSON,
      data: JSON.stringify(enrichmentRequest),
    },
  );
}

export function getEnrichmentForObjecttypeId(
  workbookNameAndHash: WorkbookNameAndHash,
  objecttypeId: ObjecttypeIdString,
): Promise<EnrichmentDto> {
  return ajax<EnrichmentDto>(
    `/workbooks/${workbookNameAndHashString(workbookNameAndHash)}/objecttypes/${window.btoa(objecttypeId)}/enrichment`,
    {
      headers: { accept: MIMETYPE_JSON },
    },
  );
}

export function getDataScopeContextMapping(
  workbookNameAndHash: WorkbookNameAndHash,
  worksheetId: WorksheetId,
  objecttypeId: ObjecttypeIdString,
  existingEnrichmentObjecttypeId?: ObjecttypeIdString,
) {
  const urlParams = new URLSearchParams();
  urlParams.set('worksheetId', worksheetId);
  if (existingEnrichmentObjecttypeId) {
    urlParams.set('existingEnrichmentObjecttypeId', window.btoa(existingEnrichmentObjecttypeId));
  }
  return ajax<DataScopeContextMapping>(
    `/workbooks/${workbookNameAndHashString(workbookNameAndHash)}/objecttypes/${window.btoa(objecttypeId)}/enrichment-data-scope-contexts?${urlParams}`,
  );
}
